/** "Focus on the current week; collapse anything older."
 *
 * Alan (2026-08-25): "some of the screens are getting long. focus on current
 * week and collapse history longer than 5 business days."
 *
 * Purely presentational — nothing here filters what is FETCHED or what any
 * total is computed from. The older rows are still rendered, just behind a
 * toggle, so a figure in a header can never disagree with the list under it.
 */

export const RECENT_BUSINESS_DAYS = 5;

function isBusinessDay(d: Date): boolean {
  const wd = d.getDay();
  return wd !== 0 && wd !== 6;
}

/** Local midnight of the Nth-most-recent business day, counting today when
 *  today is one. With N=5 on a Tuesday that reaches back to the previous
 *  Wednesday — this week plus the tail of last week, which is what "the
 *  current week" means on a Monday morning. Weekend rows inside the window
 *  still show: crypto and forex trade days the equity calendar skips. */
export function businessDayCutoff(days = RECENT_BUSINESS_DAYS, now: Date = new Date()): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  let counted = isBusinessDay(d) ? 1 : 0;
  while (counted < days) {
    d.setDate(d.getDate() - 1);
    if (isBusinessDay(d)) counted++;
  }
  return d;
}

/** Split a chronological list into what's in the window and what's older.
 *  Order is preserved within each half. An item whose timestamp is missing or
 *  unparseable counts as RECENT — never hide something we couldn't date. */
export function splitRecent<T>(
  items: T[],
  at: (item: T) => string | number | Date | null | undefined,
  days = RECENT_BUSINESS_DAYS,
  now: Date = new Date(),
): { recent: T[]; older: T[] } {
  const cutoff = businessDayCutoff(days, now).getTime();
  const recent: T[] = [];
  const older: T[] = [];
  for (const item of items) {
    const raw = at(item);
    const t = raw == null ? NaN : new Date(raw).getTime();
    (Number.isNaN(t) || t >= cutoff ? recent : older).push(item);
  }
  return { recent, older };
}
